import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";

import { AddressAutocompleteV2 } from "components/AddressAutocompleteV2";
import { ManualRoutePointInput } from "components/ManualRoutePointInput";

import { TimeInput } from "../../../../../TimeInput";
import { getTimeInputLabel } from "../../utils";
import { AddStopButton } from "../AddStopButton";

import type { SearchBoxFeatureSuggestion } from "@mapbox/search-js-core";

interface Props {
  routePoint: EditableRoutePointState;
  error: string | null;
  onRoutePointMetadataChange: (
    key: "start_time" | "stop_operation" | "schedule_type",
    value: string,
  ) => void;
  onAddressSuggestionSelected: (feature: SearchBoxFeatureSuggestion) => void;
  onAddressCleared: () => void;
  onSubmit: VoidFunction;
  updateInputModeForRoutePoint: (mode: "autocomplete" | "manual") => void;
  onUpdatePointLocation: <T>(key: keyof RoutePointLocation, value: T) => void;
}

export function RoutesForm(props: Props) {
  const {
    error,
    routePoint,
    onSubmit,
    onRoutePointMetadataChange,
    onAddressSuggestionSelected,
    onAddressCleared,
    updateInputModeForRoutePoint,
    onUpdatePointLocation,
  } = props;

  const onStopOperationChanged = (value: string) => {
    if (value === "PICKUP") {
      /* if the stop operation changed to pickup, update
      the schedule type to DATETIME, as ASAP is not allowed for pickups.
      */
      onRoutePointMetadataChange("schedule_type", "DATETIME");
    } else if (value === "DROPOFF") {
      // if value changed to dropoff, choose ASAP as the default
      onRoutePointMetadataChange("schedule_type", "ASAP");
    }

    onRoutePointMetadataChange("stop_operation", value);
  };

  let inputComponent = <p>Error</p>;
  if (routePoint.inputMode === "autocomplete") {
    inputComponent = (
      <AddressAutocompleteV2
        routePoint={routePoint}
        onSuggestionSelected={onAddressSuggestionSelected}
        onClear={onAddressCleared}
        updateInputModeForRoutePoint={updateInputModeForRoutePoint}
      />
    );
  } else if (routePoint.inputMode === "manual") {
    inputComponent = (
      <ManualRoutePointInput
        routePointLocation={routePoint.point_location as RoutePointLocation}
        handleRoutePointLocationValueChange={onUpdatePointLocation}
        updateInputModeForRoutePoint={updateInputModeForRoutePoint}
      />
    );
  }

  return (
    <Stack mb="2rem" spacing={4}>
      <FormControl>
        <FormLabel>Operation</FormLabel>
        <RadioGroup
          row
          value={routePoint.stop_operation}
          onChange={(e) => onStopOperationChanged(e.target.value)}
        >
          <FormControlLabel value="PICKUP" control={<Radio />} label="Pickup" />
          <FormControlLabel
            value="DROPOFF"
            control={<Radio />}
            label="Drop Off"
          />
        </RadioGroup>
      </FormControl>
      {inputComponent}
      {routePoint.stop_operation === "DROPOFF" && (
        <FormControl>
          <FormLabel>Delivery Type</FormLabel>
          <RadioGroup
            row
            value={routePoint.schedule_type}
            onChange={(e) =>
              onRoutePointMetadataChange("schedule_type", e.target.value)
            }
          >
            <FormControlLabel
              value="ASAP"
              control={<Radio />}
              label="Flexible"
            />
            <FormControlLabel
              value="DATETIME"
              control={<Radio />}
              label="Specific Date"
            />
          </RadioGroup>
          {routePoint.schedule_type === "ASAP" && (
            <Typography variant="body2" color="textSecondary">
              Flexible means you do not require delivery on a specific date. The
              carrier will provide a delivery ETA in their quote.
            </Typography>
          )}
          {routePoint.schedule_type === "DATETIME" && (
            <Typography variant="body2" color="textSecondary">
              Specific Date is the date you want your shipment delivered.
            </Typography>
          )}{" "}
        </FormControl>
      )}
      {routePoint.schedule_type !== "ASAP" && (
        <TimeInput
          timeFormat="date"
          initialValue={
            routePoint?.start_time ?? DateTime.now().toFormat("yyyy-MM-dd")
          }
          label={getTimeInputLabel(routePoint)}
          onChange={(value) => onRoutePointMetadataChange("start_time", value)}
        />
      )}
      {error != null && (
        <Typography sx={{ mt: "1rem", mb: "1rem" }} color="red">
          {error}
        </Typography>
      )}
      <AddStopButton onClick={onSubmit} />
    </Stack>
  );
}
